import { createSlice } from '@reduxjs/toolkit';

// Tiny in-house toast system — replaces react-toastify because that
// library's v10 + React 19 combination ended up neither auto-closing nor
// closing on click in our app. We just track an array of active toasts
// in Redux; rendering + auto-timeout are handled by Carbon's
// ToastNotification (which has a native `timeout` prop) via the
// <ToastStack /> component mounted at the app root.
let _toastSeq = 0;
const nextToastId = () => ++_toastSeq;

// Coerce an arbitrary value into a string the way a toast title/caption
// can safely be passed to Carbon's <ToastNotification>. RTK Query
// rejections often hand us `{message, status}` or similar objects via
// `error.data`; rendering those directly as JSX children blows up with
// React error #31 ("Objects are not valid as a React child"). This helper
// is the last-line defence — even if a call site forgets to flatten
// its error, the user gets a readable toast instead of an ErrorBoundary.
const safeText = (v) => {
  if (v == null) return '';
  const t = typeof v;
  if (t === 'string') return v;
  if (t === 'number' || t === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(safeText).filter(Boolean).join(' ');
  if (t === 'object') {
    if (typeof v.message === 'string') return v.message;
    if (typeof v.msg === 'string') return v.msg;
    if (typeof v.error === 'string') return v.error;
    try { return JSON.stringify(v); } catch { return ''; }
  }
  return String(v);
};

const toastsSlice = createSlice({
  name: "toasts",
  initialState: { items: [] },
  reducers: {
    addToast: (state, action) => {
      state.items.push({
        id: nextToastId(),
        kind: action.payload.kind || 'info',
        title: safeText(action.payload.title),
        caption: safeText(action.payload.caption || action.payload.subtitle),
      });
    },
    dismissToast: (state, action) => {
      state.items = state.items.filter((t) => t.id !== action.payload);
    },
  },
});

export const { addToast, dismissToast } = toastsSlice.actions;

// Public action creator — keeps the existing call sites
// (`dispatch(showToast({kind, title, caption}))`) intact.
export const showToast = (props = { kind: "success", title: "", caption: "" }) => {
  return addToast({ ...props });
};

export default toastsSlice.reducer;
